import type { ReactNode } from 'react';
import { Button, Card, Tag } from 'antd-mobile';
import {
  CheckCircleFill,
  ExclamationCircleOutline,
} from 'antd-mobile-icons';
import styled from 'styled-components';
import type { ScooterAccessState } from '../../services/api/types';
import {
  resolveRideReadinessPresentation,
  type PanelTone,
} from '../../utils/rideReadiness';

interface ReadinessRequirement {
  key: string;
  label: ReactNode;
  ready: boolean;
  detail: ReactNode;
}

interface RideReadinessPanelProps {
  isFleetUser: boolean;
  hasEnergyPack: boolean;
  hasScooterPack: boolean;
  accessState?: ScooterAccessState | null;
  readinessReady?: boolean | null;
  energyPackOverageAh?: number;
  scooterPackOverageKm?: number;
  loading?: boolean;
  error?: string | null;
  onBuyEnergyPack?: () => void;
  onBuyScooterPack?: () => void;
}

const panelToneBorder: Record<PanelTone, string> = {
  ready: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  restricted: '#7c3aed',
  loading: '#1677ff',
};

const PanelShell = styled(Card)<{ $tone: PanelTone }>`
  margin-bottom: 16px;
  border-left: 4px solid ${props => panelToneBorder[props.$tone]};
  border-radius: 12px;
  overflow: hidden;

  .adm-card-body {
    padding: 18px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
`;

const TitleGroup = styled.div`
  min-width: 0;
`;

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.2;
`;

const Summary = styled.div`
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
  margin-top: 6px;
`;

const RequirementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const RequirementItem = styled.div<{ $ready: boolean }>`
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  border: 1px solid ${props => props.$ready ? '#bbf7d0' : '#fecaca'};
  border-radius: 8px;
  background: ${props => props.$ready ? '#f7fef9' : '#fff7f7'};
`;

const RequirementIcon = styled.div<{ $ready: boolean }>`
  color: ${props => props.$ready ? '#16a34a' : '#dc2626'};
  font-size: 16px;
  line-height: 1;
  margin-top: 1px;
`;

const RequirementText = styled.div`
  min-width: 0;
`;

const RequirementLabel = styled.div`
  color: #1f2937;
  font-size: 13px;
  font-weight: 750;
  line-height: 1.25;
`;

const RequirementDetail = styled.div`
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 2px;
`;

const Actions = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: ${props => props.$count > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)'};
  gap: 8px;
  margin-top: 14px;

  .adm-button {
    min-height: 44px;
    font-weight: 700;
  }

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const StatusTag = styled(Tag)<{ $tone: PanelTone }>`
  ${props => props.$tone === 'restricted' ? `
    color: #6d28d9;
    background: #f5f3ff;
    border-color: #ddd6fe;
  ` : ''}
`;

const formatEnergyPackNeed = (value: number) => `${value.toFixed(1)} Ah of previous usage will be rolled over to the next pack(s)`;
const formatScooterPackNeed = (value: number) => `${value.toFixed(1)} km of previous usage will be rolled over to the next pack(s)`;

const RideReadinessPanel = ({
  isFleetUser,
  hasEnergyPack,
  hasScooterPack,
  accessState,
  readinessReady,
  energyPackOverageAh = 0,
  scooterPackOverageKm = 0,
  loading = false,
  error,
  onBuyEnergyPack,
  onBuyScooterPack,
}: RideReadinessPanelProps) => {
  const needsEnergyPackForUsage = energyPackOverageAh > 0;
  const needsScooterPackForUsage = scooterPackOverageKm > 0;

  const requirements: ReadinessRequirement[] = [
    {
      key: 'energy-pack',
      label: 'Energy pack',
      ready: hasEnergyPack && !needsEnergyPackForUsage,
      detail: needsEnergyPackForUsage
        ? formatEnergyPackNeed(energyPackOverageAh)
        : hasEnergyPack
          ? 'Active for this scooter'
          : 'Energy pack required before riding',
    },
  ];

  if (isFleetUser) {
    requirements.push({
      key: 'scooter-pack',
      label: 'Scooter pack',
      ready: hasScooterPack && !needsScooterPackForUsage,
      detail: needsScooterPackForUsage
        ? formatScooterPackNeed(scooterPackOverageKm)
        : hasScooterPack
          ? 'Active for this scooter'
          : 'Scooter pack required before riding',
    });
  }

  const unresolved = requirements.filter(requirement => !requirement.ready);
  const presentation = resolveRideReadinessPresentation({
    accessState,
    readinessReady,
    loading,
    error,
    unresolvedCount: unresolved.length,
  });
  const suppressPurchaseActions = accessState === 'admin_blocked';

  const actions = [
    ...(!suppressPurchaseActions && !requirements[0].ready && onBuyEnergyPack
      ? [{ key: 'energy-pack', label: 'Buy Energy Pack', onClick: onBuyEnergyPack }]
      : []),
    ...(!suppressPurchaseActions && isFleetUser && !requirements.find(requirement => requirement.key === 'scooter-pack')?.ready && onBuyScooterPack
      ? [{ key: 'scooter-pack', label: 'Buy Scooter Pack', onClick: onBuyScooterPack }]
      : []),
  ];

  return (
    <PanelShell $tone={presentation.tone}>
      <Header>
        <TitleGroup>
          <TitleLine>
            {presentation.tone === 'ready' ? (
              <CheckCircleFill style={{ color: presentation.iconColor }} />
            ) : (
              <ExclamationCircleOutline style={{ color: presentation.iconColor }} />
            )}
            <span>{presentation.title}</span>
          </TitleLine>
          <Summary>{presentation.summary}</Summary>
        </TitleGroup>
        <StatusTag color={presentation.tagColor} $tone={presentation.tone}>
          {presentation.statusLabel}
        </StatusTag>
      </Header>

      <RequirementGrid>
        {requirements.map(requirement => (
          <RequirementItem key={requirement.key} $ready={!loading && !error && requirement.ready}>
            <RequirementIcon $ready={!loading && !error && requirement.ready}>
              {!loading && !error && requirement.ready ? <CheckCircleFill /> : <ExclamationCircleOutline />}
            </RequirementIcon>
            <RequirementText>
              <RequirementLabel>{requirement.label}</RequirementLabel>
              <RequirementDetail>{requirement.detail}</RequirementDetail>
            </RequirementText>
          </RequirementItem>
        ))}
      </RequirementGrid>

      {!loading && !error && actions.length > 0 && (
        <Actions $count={actions.length}>
          {actions.map(action => (
            <Button key={action.key} block color="primary" onClick={action.onClick}>
              {action.label}
            </Button>
          ))}
        </Actions>
      )}
    </PanelShell>
  );
};

export default RideReadinessPanel;
